import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Navbar, useAuth } from "../components/Shared";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";
import { authSignup, authLogin, authMe, patchMe, setAuthToken } from "../lib/api";
import { PilotDebugPanel } from "../components/PilotDebugPanel";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

function routeAfterAuth(u, isPilot, navigate) {
  if (u.role === "superadmin") {
    navigate("/superadmin");
    return;
  } else if (u.role === "vendor" || u.role === "company" || u.role === "admin") {
    navigate("/admin");
  } else if (isPilot && !u.vendor_id) {
    navigate("/pilot-not-joined");
  } else {
    navigate("/");
  }
}

export default function AuthPage() {
  // Sign in state
  const [siEmail, setSiEmail] = useState("demo@prepmate.com");
  const [siPassword, setSiPassword] = useState("demo123");

  // Sign up state
  const [role, setRole] = useState("customer");
  const [name, setName] = useState("");
  const [suEmail, setSuEmail] = useState("");
  const [suPassword, setSuPassword] = useState("");
  const [location, setLocation] = useState("");

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { setToken, setUser, isPilot, setLastAuthError } = useAuth();

  async function onSignIn(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const { access_token } = await authLogin({ email: siEmail, password: siPassword });
      setToken(access_token);
      localStorage.setItem("prepmate_token", access_token);
      setAuthToken(access_token);
      setLastAuthError(null);
      const u = await authMe();
      setUser(u);
      toast({ title: "Signed in", description: `Welcome ${siEmail}` });
      routeAfterAuth(u, isPilot, navigate);
    } catch (err) {
      const errorMsg = err?.response?.data?.detail || "Invalid credentials";
      setLastAuthError(errorMsg);
      toast({ title: "Authentication failed", description: errorMsg });
    } finally {
      setLoading(false);
    }
  }

  async function onSignUp(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const { access_token } = await authSignup({ name, email: suEmail, password: suPassword, role });
      setToken(access_token);
      localStorage.setItem("prepmate_token", access_token);
      setAuthToken(access_token);
      setLastAuthError(null);
      const u = await authMe();
      if (location) {
        await patchMe({ location });
        const updated = await authMe();
        setUser(updated);
        toast({ title: "Account created", description: `Welcome ${name || suEmail}` });
        routeAfterAuth(updated, isPilot, navigate);
      } else {
        setUser(u);
        toast({ title: "Account created", description: `Welcome ${name || suEmail}` });
        routeAfterAuth(u, isPilot, navigate);
      }
    } catch (err) {
      const errorMsg = err?.response?.data?.detail || "Invalid credentials";
      setLastAuthError(errorMsg);
      toast({ title: "Authentication failed", description: errorMsg });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <Navbar />
      <div className="mx-auto max-w-md px-4 py-10">
        <Tabs defaultValue="signin">
          <TabsList className="w-full mb-6">
            <TabsTrigger value="signin" className="flex-1">Sign in</TabsTrigger>
            <TabsTrigger value="signup" className="flex-1">Create account</TabsTrigger>
          </TabsList>

          {/* ── Sign In ── */}
          <TabsContent value="signin">
            <form onSubmit={onSignIn} className="space-y-3">
              <input
                value={siEmail}
                onChange={e => setSiEmail(e.target.value)}
                placeholder="Email"
                className="w-full border rounded-md p-2 bg-white"
                data-testid="auth-email-input"
              />
              <input
                type="password"
                value={siPassword}
                onChange={e => setSiPassword(e.target.value)}
                placeholder="Password"
                className="w-full border rounded-md p-2 bg-white"
                data-testid="auth-password-input"
              />
              <Button
                type="submit"
                className="w-full bg-[#2a7d4f] text-white hover:bg-[#236840]"
                disabled={loading}
                data-testid="auth-submit-btn"
              >
                {loading ? "Please wait..." : "Continue"}
              </Button>
            </form>
            <p className="text-xs text-neutral-500 mt-3">
              Vendor? Your role is detected automatically at sign in.
            </p>
          </TabsContent>

          {/* ── Create Account ── */}
          <TabsContent value="signup">
            {/* Role selection cards */}
            <div className="grid grid-cols-2 gap-3 mb-5">
              <button
                type="button"
                onClick={() => setRole("customer")}
                className={`rounded-xl border-2 p-4 text-left transition-all ${
                  role === "customer"
                    ? "border-[#2a7d4f] bg-[#2a7d4f] text-white"
                    : "border-neutral-200 bg-white hover:border-neutral-400"
                }`}
              >
                <div className="text-lg mb-1">🛒</div>
                <div className="font-semibold text-sm">Customer</div>
                <div className={`text-xs mt-0.5 ${role === "customer" ? "text-white/70" : "text-neutral-500"}`}>
                  Browse &amp; subscribe
                </div>
              </button>
              <button
                type="button"
                onClick={() => setRole("company")}
                className={`rounded-xl border-2 p-4 text-left transition-all ${
                  role === "company"
                    ? "border-[#2a7d4f] bg-[#2a7d4f] text-white"
                    : "border-neutral-200 bg-white hover:border-neutral-400"
                }`}
              >
                <div className="text-lg mb-1">🍱</div>
                <div className="font-semibold text-sm">Meal Prep Company</div>
                <div className={`text-xs mt-0.5 ${role === "company" ? "text-white/70" : "text-neutral-500"}`}>
                  Manage your menu
                </div>
              </button>
            </div>

            <form onSubmit={onSignUp} className="space-y-3">
              <input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Name"
                className="w-full border rounded-md p-2 bg-white"
                data-testid="signup-name-input"
              />
              <input
                value={suEmail}
                onChange={e => setSuEmail(e.target.value)}
                placeholder="Email"
                className="w-full border rounded-md p-2 bg-white"
                data-testid="auth-email-input"
              />
              <input
                type="password"
                value={suPassword}
                onChange={e => setSuPassword(e.target.value)}
                placeholder="Password"
                className="w-full border rounded-md p-2 bg-white"
                data-testid="auth-password-input"
              />
              <input
                value={location}
                onChange={e => setLocation(e.target.value)}
                placeholder="City or ZIP (for vendor filtering)"
                className="w-full border rounded-md p-2 bg-white"
              />
              <label className="flex items-start gap-2 text-xs text-neutral-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={e => setTermsAccepted(e.target.checked)}
                  className="mt-0.5 shrink-0"
                />
                <span>
                  I agree to the{" "}
                  <Link to="/terms" className="underline hover:text-black" target="_blank" rel="noopener noreferrer">Terms of Service</Link>
                  {" "}and{" "}
                  <Link to="/privacy" className="underline hover:text-black" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>.
                  I acknowledge that PrepMate is a technology marketplace and is not responsible for food products prepared by independent vendors.
                </span>
              </label>
              <Button
                type="submit"
                className="w-full bg-[#2a7d4f] text-white hover:bg-[#236840]"
                disabled={loading || !termsAccepted}
                data-testid="auth-submit-btn"
              >
                {loading ? "Please wait..." : "Continue"}
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </div>
      <PilotDebugPanel />
    </div>
  );
}
